import { useState, type ReactNode } from 'react';
import { isAxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import type {
  FulfillmentOrderLine,
  LineSourcingComparison,
  SourcingPlatform,
  SourcingSellerType,
  SourcingStockStatus,
  UpdateLineSourcingDetailRequest,
} from '@/infrastructure/FulfillmentAdminClient';
import { serverRefusal } from '@/utils/fulfillmentErrors';
import { kstTimestamp } from '@/utils/fulfillmentTime';
import {
  OUTCOME_LABELS,
  SELLER_TYPE_LABELS,
  SELLER_TYPE_OPTION_LABELS,
  SELLER_TYPES_BY_PLATFORM,
  SOURCING_ALTERNATIVES_MAX,
  SOURCING_PLATFORM_LABELS,
  SOURCING_PLATFORMS,
  SOURCING_STOCK_STATUSES,
  STOCK_STATUS_LABELS,
  blankAlternativeDraft,
  describeSourcingRefusal,
  hasSourcingDraftErrors,
  landedTotalOf,
  sellerTypeLabel,
  sellsOn,
  sourcingDraftErrorsOf,
  sourcingDraftOf,
  sourcingRequestOf,
  withOptionNames,
  type SourcingAlternativeDraft,
  type SourcingAlternativeField,
  type SourcingDetailDraft,
  type SourcingOutcomeChoice,
} from '@/utils/fulfillmentSourcingDetail';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';
import { CheckIcon, CloseIcon } from '@/pages/fulfillment/components/UltimateIcons';
import './lineSourcingDetail.css';

const TITLE_ID = 'line-sourcing-detail-title';
const DASH = '—';
const SAVE_FAILED = 'The sourcing detail was not saved. Reload the page and try again.';

const OUTCOME_CHOICES: Array<{ value: SourcingOutcomeChoice; label: string; hint: string }> = [
  { value: 'COMPARED', label: OUTCOME_LABELS.COMPARED, hint: 'two or more options, one bought' },
  {
    value: 'NO_ALTERNATIVE_AVAILABLE',
    label: OUTCOME_LABELS.NO_ALTERNATIVE_AVAILABLE,
    hint: 'others checked, none in stock with a price',
  },
  { value: 'NOT_COMPARED', label: OUTCOME_LABELS.NOT_COMPARED, hint: 'nothing else was checked' },
  { value: '', label: 'No comparison recorded', hint: 'saving clears a stored one' },
];

interface ConflictBody {
  code?: string;
  changedBy?: string;
  rule?: string;
  message?: string | string[];
}

/** Why the last save did not land; a conflict also means the line on screen was re-read. */
type SaveRefusal = { kind: 'conflict' | 'invalid' | 'failed'; text: string };

interface LineSourcingDetailModalProps {
  readonly line: FulfillmentOrderLine;
  /** Set when the line can only be read (not JIT, or cancelled): the dialog shows what is recorded and why it stops there. */
  readonly readOnlyReason?: string | undefined;
  readonly onHide: () => void;
  /** Writes the detail; rejects with the server's refusal. */
  readonly onSave: (request: UpdateLineSourcingDetailRequest) => Promise<unknown>;
  /** Called once the write landed; the caller re-reads the order and closes the dialog. */
  readonly onSaved: () => void | Promise<void>;
  /** Re-reads the order after a 409, so `line` (and its updatedAt) becomes what is stored now; resolves whether the re-read landed. */
  readonly onConflict: () => Promise<boolean>;
}

const won = (value: number | undefined): string => (value === undefined ? DASH : `₩${value.toLocaleString('en-US')}`);

/** What is stored now, as read: the panel the operator checks before and after a save. */
const RecordedDetail = ({
  sellerType,
  comparison,
}: {
  sellerType: SourcingSellerType | null | undefined;
  comparison: LineSourcingComparison | null | undefined;
}) => {
  if (!sellerType && !comparison) {
    return <p className="text-muted small mb-0">Nothing recorded yet.</p>;
  }
  // Expected arrival and Qty limit are no longer asked (SSD-7672); a record that still holds them shows them.
  const legacyArrival = comparison?.alternatives.some((alternative) => alternative.expectedArrivalDate !== undefined);
  const legacyLimit = comparison?.alternatives.some((alternative) => alternative.quantityLimit !== undefined);
  return (
    <div data-testid="sourcing-recorded">
      <dl className="row small mb-2">
        <dt className="col-sm-3">Seller type</dt>
        <dd className="col-sm-9">{sellerTypeLabel(sellerType)}</dd>
        <dt className="col-sm-3">Comparison</dt>
        <dd className="col-sm-9">
          {comparison ? (OUTCOME_LABELS[comparison.outcome] ?? comparison.outcome) : 'Not recorded'}
        </dd>
        {comparison && (
          <>
            {/* Kept for reference only (SSD-7672): shown on a record that has one. */}
            {comparison.requiredArrivalDate !== undefined && (
              <>
                <dt className="col-sm-3">Required arrival</dt>
                <dd className="col-sm-9">{comparison.requiredArrivalDate}</dd>
              </>
            )}
            <dt className="col-sm-3">Recorded</dt>
            <dd className="col-sm-9">
              {kstTimestamp(comparison.capturedAt)} by {comparison.capturedBy}
            </dd>
          </>
        )}
      </dl>
      {comparison && comparison.alternatives.length > 0 && (
        <div className="sourcing-options-scroll" role="region" aria-label="Recorded options table" tabIndex={0}>
          <Table
            size="sm"
            bordered
            className={`small mb-0 sourcing-options sourcing-recorded-options so-w-${legacyArrival ? (legacyLimit ? 'both' : 'arrival') : legacyLimit ? 'limit' : 'base'}`}
            aria-label="Recorded options"
          >
            {/* The options table's first nine columns at the same scale (SSD-7672), so a recorded value sits over its box. */}
            <colgroup>
              <col className="so-n" />
              <col className="so-bought" />
              <col className="so-platform" />
              <col className="so-seller" />
              <col className="so-pieces" />
              <col className="so-items" />
              <col className="so-fee" />
              <col className="so-landed" />
              <col className="so-stock" />
              {legacyArrival && <col className="so-arrives" />}
              {legacyLimit && <col className="so-limit" />}
            </colgroup>
            <thead>
              <tr>
                <th>#</th>
                <th>Bought</th>
                <th>Platform</th>
                <th>Seller type</th>
                <th className="text-end">Pieces</th>
                <th className="text-end">Items</th>
                <th className="text-end">Delivery</th>
                <th className="text-end">Landed</th>
                <th>Stock</th>
                {legacyArrival && <th>Arrives</th>}
                {legacyLimit && <th className="text-end">Limit</th>}
              </tr>
            </thead>
            <tbody>
              {comparison.alternatives.map((alternative, index) => (
                <tr key={alternative.alternativeId}>
                  <td>{index + 1}</td>
                  <td>
                    {alternative.alternativeId === comparison.chosenAlternativeId && (
                      <span className="sourcing-bought-mark">
                        <CheckIcon aria-hidden="true" />
                        <span className="visually-hidden">Bought</span>
                      </span>
                    )}
                  </td>
                  <td>{SOURCING_PLATFORM_LABELS[alternative.platform] ?? alternative.platform}</td>
                  <td>{sellerTypeLabel(alternative.sellerType)}</td>
                  <td className="text-end">{alternative.quantityBasis}</td>
                  <td className="text-end">{won(alternative.itemsTotalKrw)}</td>
                  <td className="text-end">{won(alternative.deliveryFeeKrw)}</td>
                  <td className="text-end">{won(alternative.landedTotalKrw)}</td>
                  <td>{STOCK_STATUS_LABELS[alternative.stockStatus] ?? alternative.stockStatus}</td>
                  {legacyArrival && <td>{alternative.expectedArrivalDate ?? DASH}</td>}
                  {legacyLimit && <td className="text-end">{alternative.quantityLimit ?? 'No limit'}</td>}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

/** One box of an option row with the sentence standing against it. */
const OptionCell = ({ error, children }: { error: string | undefined; children: ReactNode }) => (
  <td>
    {children}
    {error && <div className="invalid-feedback d-block">{error}</div>}
  </td>
);

/**
 * Where a JIT line was bought and what else was on offer at that moment (SSD-7177): the seller type and the
 * purchase-time price comparison, recorded by the Order Team through the sourcingDetail route.
 */
export function LineSourcingDetailModal({
  line,
  readOnlyReason,
  onHide,
  onSave,
  onSaved,
  onConflict,
}: LineSourcingDetailModalProps) {
  const editable = readOnlyReason === undefined;
  const [draft, setDraft] = useState<SourcingDetailDraft>(() => sourcingDraftOf(line));
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<SaveRefusal | null>(null);

  const errors = sourcingDraftErrorsOf(draft);
  const shown = submitted ? errors : { alternatives: {}, form: [] };
  // The last save wins (SSD-7665): no expected version is sent, and only the part the operator changed since this
  // opened travels, so a seller type or comparison stored meanwhile by someone else stays unless both changed it.
  const [opened, setOpened] = useState<UpdateLineSourcingDetailRequest>(() =>
    sourcingRequestOf(sourcingDraftOf(line), undefined),
  );
  const full = sourcingRequestOf(draft, undefined);
  const request: UpdateLineSourcingDetailRequest = {
    ...(JSON.stringify(full.sellerType) !== JSON.stringify(opened.sellerType) && { sellerType: full.sellerType }),
    ...(JSON.stringify(full.sourcingComparison) !== JSON.stringify(opened.sourcingComparison) && {
      sourcingComparison: full.sourcingComparison,
    }),
  };
  const dirty = Object.keys(request).length > 0;

  const update = (changes: Partial<SourcingDetailDraft>) => setDraft((current) => ({ ...current, ...changes }));

  const updateOption = (alternativeId: string, changes: Partial<SourcingAlternativeDraft>) =>
    setDraft((current) => ({
      ...current,
      alternatives: current.alternatives.map((alternative) => {
        if (alternative.alternativeId !== alternativeId) {
          return alternative;
        }
        const next = { ...alternative, ...changes };
        // A seller type that the new platform cannot have would only be refused, so it goes with the old platform.
        if (next.platform !== '' && next.sellerType !== '' && !sellsOn(next.platform, next.sellerType)) {
          next.sellerType = '';
        }
        return next;
      }),
    }));

  const choose = (alternative: SourcingAlternativeDraft) =>
    setDraft((current) => ({
      ...current,
      chosenAlternativeId: alternative.alternativeId,
      // The line's seller type is the bought option's; fill it when the operator has not said one yet.
      sellerType:
        current.sellerType === '' && alternative.sellerType !== '' ? alternative.sellerType : current.sellerType,
    }));

  const addOption = () =>
    setDraft((current) => ({
      ...current,
      alternatives: [...current.alternatives, blankAlternativeDraft(uuidv4(), line.totalQuantity)],
    }));

  const removeOption = (alternativeId: string) =>
    setDraft((current) => ({
      ...current,
      alternatives: current.alternatives.filter((alternative) => alternative.alternativeId !== alternativeId),
      chosenAlternativeId: current.chosenAlternativeId === alternativeId ? '' : current.chosenAlternativeId,
    }));

  const save = async () => {
    setSubmitted(true);
    if (hasSourcingDraftErrors(errors) || !dirty) {
      return;
    }
    setSaving(true);
    setRefusal(null);
    try {
      await onSave(request);
    } catch (err) {
      const failure = isAxiosError<ConflictBody>(err) ? err : undefined;
      const status = failure?.response?.status;
      const body = failure?.response?.data;
      if (status === 409) {
        const who = body?.changedBy ? `by ${body.changedBy}` : 'by someone else';
        if (await onConflict()) {
          setRefusal({
            kind: 'conflict',
            text: `Not saved: line ${line.lineNo} was changed ${who} after you opened it. The order was reloaded and "Recorded now" shows what is stored. Your edits are still here — check them against it and save again, or load the recorded values.`,
          });
        } else {
          setRefusal({
            kind: 'failed',
            text: `Not saved: line ${line.lineNo} was changed ${who} after you opened it, and the order could not be reloaded. Reload the page before saving again.`,
          });
        }
      } else if (status === 400) {
        setRefusal({
          kind: 'invalid',
          text: `Not saved: ${withOptionNames(describeSourcingRefusal(body) ?? SAVE_FAILED, draft)}`,
        });
      } else {
        setRefusal({ kind: 'failed', text: serverRefusal(err) ?? SAVE_FAILED });
      }
      setSaving(false);
      return;
    }
    setSaving(false);
    await onSaved();
  };

  const loadRecorded = () => {
    setDraft(sourcingDraftOf(line));
    setOpened(sourcingRequestOf(sourcingDraftOf(line), undefined));
    setSubmitted(false);
    setRefusal(null);
  };

  const comparing = draft.outcome !== '';
  const optionError = (alternativeId: string, field: SourcingAlternativeField): string | undefined =>
    shown.alternatives[alternativeId]?.[field];
  // Expected arrival and Qty limit have no box any more (SSD-7672), so a stored value the checks refuse is named here.
  const hiddenErrors = draft.alternatives.flatMap((alternative, index) =>
    (
      [
        ['expectedArrivalDate', 'expected arrival', alternative.expectedArrivalDate],
        ['quantityLimit', 'quantity limit', alternative.quantityLimit],
      ] as const
    )
      .filter(([field]) => optionError(alternative.alternativeId, field) !== undefined)
      .map(
        ([field, name, value]) =>
          `Option ${index + 1}: the stored ${name} "${value}" is refused (${optionError(alternative.alternativeId, field)}). Remove the option and add it again.`,
      ),
  );

  return (
    <Modal
      {...ULTIMATE_MODAL}
      show
      onHide={onHide}
      size="xl"
      dialogClassName="sourcing-detail-dialog"
      aria-labelledby={TITLE_ID}
      backdrop={saving ? 'static' : true}
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title id={TITLE_ID}>
          Sourcing detail — line {line.lineNo} · {line.sku}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted mb-2">
          {line.productName ?? line.sku} · qty {line.totalQuantity} · route {line.sourcingRoute ?? DASH}
        </p>
        <h6 className="sourcing-section">Recorded now</h6>
        <RecordedDetail sellerType={line.sellerType} comparison={line.sourcingComparison} />
        {!editable && (
          <Alert variant="secondary" className="mt-3 mb-0 small">
            {readOnlyReason}
          </Alert>
        )}
        {editable && (
          <Form
            className="mt-3"
            noValidate
            // Enter in a box must not navigate away; the footer button is the one way to save.
            onSubmit={(event) => event.preventDefault()}
          >
            <h6 className="sourcing-section">Record</h6>
            <Form.Group className="mb-3 sourcing-seller-group" controlId="sourcing-seller-type">
              <Form.Label>Seller type the line was bought from</Form.Label>
              <Form.Select
                size="sm"
                value={draft.sellerType}
                aria-invalid={shown.sellerType !== undefined}
                isInvalid={shown.sellerType !== undefined}
                onChange={(event) => update({ sellerType: event.target.value as SourcingSellerType | '' })}
              >
                <option value="">Not recorded</option>
                {SOURCING_PLATFORMS.map((platform) => (
                  <optgroup key={platform} label={SOURCING_PLATFORM_LABELS[platform]}>
                    {SELLER_TYPES_BY_PLATFORM[platform].map((sellerType) => (
                      <option key={sellerType} value={sellerType}>
                        {SELLER_TYPE_OPTION_LABELS[sellerType]}
                      </option>
                    ))}
                  </optgroup>
                ))}
                <option value="UNKNOWN">{SELLER_TYPE_LABELS.UNKNOWN}</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">{shown.sellerType}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="d-block">Purchase-time price comparison</Form.Label>
              <div className="sourcing-outcomes">
                {OUTCOME_CHOICES.map((choice) => (
                  <Form.Check
                    key={choice.value || 'none'}
                    className="mb-0"
                    type="radio"
                    id={`sourcing-outcome-${choice.value || 'none'}`}
                    name="sourcing-outcome"
                    label={
                      <>
                        {choice.label} <span className="text-muted">({choice.hint})</span>
                      </>
                    }
                    checked={draft.outcome === choice.value}
                    onChange={() => update({ outcome: choice.value })}
                  />
                ))}
              </div>
              {shown.outcome && <div className="invalid-feedback d-block">{shown.outcome}</div>}
            </Form.Group>

            {comparing && (
              <>
                <div className="sourcing-options-scroll" role="region" aria-label="Options table" tabIndex={0}>
                  <Table size="sm" bordered className="small mb-2 align-top sourcing-options" aria-label="Options">
                    {/* Fixed widths measured on the longest values (SSD-7672): picking or typing never moves a column. */}
                    <colgroup>
                      <col className="so-n" />
                      <col className="so-bought" />
                      <col className="so-platform" />
                      <col className="so-seller" />
                      <col className="so-pieces" />
                      <col className="so-items" />
                      <col className="so-fee" />
                      <col className="so-landed" />
                      <col className="so-stock" />
                      <col className="so-remove" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Bought</th>
                        <th>Platform</th>
                        <th>Seller type</th>
                        <th>Pieces</th>
                        <th>Items ₩</th>
                        <th>Delivery ₩</th>
                        <th className="text-end">Landed</th>
                        <th>Stock</th>
                        <th aria-label="Remove" />
                      </tr>
                    </thead>
                    <tbody>
                      {draft.alternatives.map((alternative, index) => {
                        const n = index + 1;
                        const landed = landedTotalOf(alternative);
                        return (
                          <tr key={alternative.alternativeId}>
                            <td>{n}</td>
                            <td>
                              <Form.Check
                                type="radio"
                                name="sourcing-chosen"
                                aria-label={`Option ${n} was bought`}
                                checked={draft.chosenAlternativeId === alternative.alternativeId}
                                onChange={() => choose(alternative)}
                              />
                            </td>
                            <OptionCell error={optionError(alternative.alternativeId, 'platform')}>
                              <Form.Select
                                size="sm"
                                aria-label={`Option ${n} platform`}
                                value={alternative.platform}
                                aria-invalid={optionError(alternative.alternativeId, 'platform') !== undefined}
                                isInvalid={optionError(alternative.alternativeId, 'platform') !== undefined}
                                onChange={(event) =>
                                  updateOption(alternative.alternativeId, {
                                    platform: event.target.value as SourcingPlatform | '',
                                  })
                                }
                              >
                                <option value="">Pick…</option>
                                {SOURCING_PLATFORMS.map((platform) => (
                                  <option key={platform} value={platform}>
                                    {SOURCING_PLATFORM_LABELS[platform]}
                                  </option>
                                ))}
                              </Form.Select>
                            </OptionCell>
                            <OptionCell error={optionError(alternative.alternativeId, 'sellerType')}>
                              <Form.Select
                                size="sm"
                                aria-label={`Option ${n} seller type`}
                                value={alternative.sellerType}
                                disabled={alternative.platform === ''}
                                aria-invalid={optionError(alternative.alternativeId, 'sellerType') !== undefined}
                                isInvalid={optionError(alternative.alternativeId, 'sellerType') !== undefined}
                                onChange={(event) =>
                                  updateOption(alternative.alternativeId, {
                                    sellerType: event.target.value as SourcingSellerType | '',
                                  })
                                }
                              >
                                <option value="">—</option>
                                {alternative.platform !== '' &&
                                  SELLER_TYPES_BY_PLATFORM[alternative.platform].map((sellerType) => (
                                    <option key={sellerType} value={sellerType}>
                                      {SELLER_TYPE_OPTION_LABELS[sellerType]}
                                    </option>
                                  ))}
                                <option value="UNKNOWN">{SELLER_TYPE_LABELS.UNKNOWN}</option>
                              </Form.Select>
                            </OptionCell>
                            <OptionCell error={optionError(alternative.alternativeId, 'quantityBasis')}>
                              <Form.Control
                                size="sm"
                                inputMode="numeric"
                                aria-label={`Option ${n} pieces priced`}
                                value={alternative.quantityBasis}
                                aria-invalid={optionError(alternative.alternativeId, 'quantityBasis') !== undefined}
                                isInvalid={optionError(alternative.alternativeId, 'quantityBasis') !== undefined}
                                onChange={(event) =>
                                  updateOption(alternative.alternativeId, { quantityBasis: event.target.value })
                                }
                              />
                            </OptionCell>
                            <OptionCell error={optionError(alternative.alternativeId, 'itemsTotalKrw')}>
                              <Form.Control
                                size="sm"
                                inputMode="numeric"
                                aria-label={`Option ${n} items total (KRW)`}
                                value={alternative.itemsTotalKrw}
                                aria-invalid={optionError(alternative.alternativeId, 'itemsTotalKrw') !== undefined}
                                isInvalid={optionError(alternative.alternativeId, 'itemsTotalKrw') !== undefined}
                                onChange={(event) =>
                                  updateOption(alternative.alternativeId, { itemsTotalKrw: event.target.value })
                                }
                              />
                            </OptionCell>
                            <OptionCell error={optionError(alternative.alternativeId, 'deliveryFeeKrw')}>
                              <Form.Control
                                size="sm"
                                inputMode="numeric"
                                aria-label={`Option ${n} delivery fee (KRW)`}
                                value={alternative.deliveryFeeKrw}
                                aria-invalid={optionError(alternative.alternativeId, 'deliveryFeeKrw') !== undefined}
                                isInvalid={optionError(alternative.alternativeId, 'deliveryFeeKrw') !== undefined}
                                onChange={(event) =>
                                  updateOption(alternative.alternativeId, { deliveryFeeKrw: event.target.value })
                                }
                              />
                            </OptionCell>
                            <td className="text-end text-nowrap" aria-label={`Option ${n} landed total`}>
                              {landed === null ? DASH : `₩${landed.toLocaleString('en-US')}`}
                            </td>
                            <OptionCell error={optionError(alternative.alternativeId, 'stockStatus')}>
                              <Form.Select
                                size="sm"
                                aria-label={`Option ${n} stock`}
                                value={alternative.stockStatus}
                                aria-invalid={optionError(alternative.alternativeId, 'stockStatus') !== undefined}
                                isInvalid={optionError(alternative.alternativeId, 'stockStatus') !== undefined}
                                onChange={(event) =>
                                  updateOption(alternative.alternativeId, {
                                    stockStatus: event.target.value as SourcingStockStatus | '',
                                  })
                                }
                              >
                                <option value="">Pick…</option>
                                {SOURCING_STOCK_STATUSES.map((stockStatus) => (
                                  <option key={stockStatus} value={stockStatus}>
                                    {STOCK_STATUS_LABELS[stockStatus]}
                                  </option>
                                ))}
                              </Form.Select>
                            </OptionCell>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                className="sourcing-remove"
                                aria-label={`Remove option ${n}`}
                                onClick={() => removeOption(alternative.alternativeId)}
                              >
                                <CloseIcon />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                      {draft.alternatives.length === 0 && (
                        <tr>
                          <td colSpan={10} className="text-center text-muted">
                            No options yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={draft.alternatives.length >= SOURCING_ALTERNATIVES_MAX}
                    onClick={addOption}
                  >
                    + Add option
                  </Button>
                  <span className="small text-muted">
                    Prices are whole KRW incl. VAT for the pieces priced; landed = items + delivery. Up to{' '}
                    {SOURCING_ALTERNATIVES_MAX} options.
                  </span>
                </div>
                {shown.chosen && <div className="invalid-feedback d-block">{shown.chosen}</div>}
                {[...shown.form, ...hiddenErrors].map((message) => (
                  <div key={message} className="invalid-feedback d-block">
                    {message}
                  </div>
                ))}
              </>
            )}
            {refusal && (
              <Alert variant={refusal.kind === 'conflict' ? 'warning' : 'danger'} className="mt-3 mb-0" role="alert">
                <div>{refusal.text}</div>
                {refusal.kind === 'conflict' && (
                  <Button size="sm" variant="outline-secondary" className="mt-2" onClick={loadRecorded}>
                    Load the recorded values
                  </Button>
                )}
              </Alert>
            )}
            {submitted && hasSourcingDraftErrors(errors) && (
              <Alert variant="danger" className="mt-3 mb-0 small">
                Fix the highlighted fields first.
              </Alert>
            )}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          {editable ? 'Cancel' : 'Close'}
        </Button>
        {editable && (
          <Button
            variant="primary"
            disabled={saving || !dirty}
            title={dirty ? '' : 'Nothing changed'}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save sourcing detail'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
